import { STATUS_CONFIG } from "./statisticsConfig";
import React, { useEffect, useState, useMemo } from "react";
import StatisticsCard from "./StatisticsCard";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

const StatisticsContainer = ({
  statsMode = "normal", // "normal" | "api" | "redux"
  api, // existing
  apiStatusCount, // existing
  reduxStats, // new
  reduxKey, // new
  keyName,
  paginatedList = [],
  showStats,
  onFilter,
  queryName,
}) => {
  const [allRecords, setAllRecords] = useState([]);
  const [statusCount, setStatusCount] = useState({});
  const [statusFilter, setStatusFilter] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    if (statsMode !== "normal") return;

    const fetchAll = async () => {
      setInitialLoading(true);
      try {
        let page = 1;
        let per_page = 200;
        let combined = [];

        while (true) {
          const payload = {
            page,
            per_page,
            searchTerm: "",
            ...(queryName ? { queryName } : {}),
          };

          const res = await api(payload);

          combined = [...combined, ...(res?.data || [])];
          if (combined.length >= (res?.total || 0)) break;
          page++;
        }

        setAllRecords(combined);
      } catch (err) {
        console.error("Stats fetch error", err);
      }

      setInitialLoading(false);
    };

    fetchAll();
  }, [statsMode, api, queryName]);

  useEffect(() => {
    if (!showStats || statsMode !== "normal") return;

    let counts = {};
    allRecords.forEach((item) => {
      const status = item?.[keyName]?.toLowerCase()?.trim();
      if (status && STATUS_CONFIG[status]) {
        counts[status] = (counts[status] || 0) + 1;
      }
    });

    setStatusCount(counts);
    // eslint-disable-next-line
  }, [showStats, statsMode, allRecords]);

  useEffect(() => {
    if (!showStats || statsMode !== "api") return;
    if (!apiStatusCount) return;

    let formatted = {};

    apiStatusCount.forEach((item) => {
      const raw = item?.status || item?.name || item?.id;
      const key = raw ? raw.toLowerCase().trim() : "no_status";

      if (STATUS_CONFIG[key]) {
        formatted[key] = item?.count || 0;
      }
    });

    setStatusCount(formatted);
    setInitialLoading(false);
  }, [showStats, statsMode, apiStatusCount]);

  useEffect(() => {
    if (!showStats || statsMode !== "redux") return;
    if (!reduxStats || !reduxKey) return;

    let formatted = {};
    const chartData = reduxStats?.[reduxKey]?.chartData || [];

    chartData.forEach((item) => {
      const raw = item?.status;
      const key = raw ? raw.toLowerCase().trim() : "no_status";

      if (STATUS_CONFIG[key]) {
        formatted[key] = item?.count || 0;
      }
    });

    setStatusCount(formatted);
    setInitialLoading(false);
  }, [showStats, statsMode, reduxStats, reduxKey]);

  const filteredData = useMemo(() => {
    if (!showStats) return paginatedList;
    if (!statusFilter) return paginatedList;

    if (statsMode === "normal") {
      return allRecords.filter(
        (item) => item?.[keyName]?.toLowerCase()?.trim() === statusFilter,
      );
    }

    if (statsMode === "api" || statsMode === "redux") {
      return paginatedList.filter((item) => {
        const rowStatus = item?.[keyName]
          ? item?.[keyName]?.toLowerCase()?.trim()
          : "no_status";
        return rowStatus === statusFilter;
      });
    }

    return paginatedList;
    // eslint-disable-next-line
  }, [statsMode, showStats, statusFilter, allRecords, paginatedList, keyName]);

  useEffect(() => {
    if (onFilter) onFilter(filteredData);
  }, [filteredData, onFilter]);

  if (!showStats) return null;

  if (initialLoading) {
    return (
      <div className="text-center w-100 py-4">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-2">Loading statistics...</p>
      </div>
    );
  }

  const statusKeys = Object.keys(statusCount);

  return (
    <div className="statistics-cards-wrapper">
      <Row className="stats-row">
        {statusKeys.map((status) => {
          const cfg = STATUS_CONFIG[status];
          if (!cfg) return null;

          return (
            <Col xs={6} sm={4} md={3} lg={2} key={status}>
              <StatisticsCard
                label={cfg.label}
                count={statusCount[status]}
                icon={cfg.icon}
                color={cfg.color}
                onClick={() =>
                  setStatusFilter((prev) => (prev === status ? null : status))
                }
              />
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default StatisticsContainer;
